/**
 * Route copy for `src/routes/design` (keys: `routes.design.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  brandKitPage: {
    logoFallback: 'logo',
    uploadingTheLogoFailed: 'Uploading the logo failed.',
    theNameIsRequired: 'The name is required.',
    savingTheBrandKitFailed: 'Saving the brand kit failed.',
    newBrandKit: 'New brand kit',
    editTheBrandKit: 'Edit the brand kit',
    cancel: 'Cancel',
    saving: 'Saving…',
    save: 'Save',
    name: 'Name',
    defaultKit: 'Default kit',
    colors: 'Colors',
    rrggbb: '#RRGGBB',
    color: '+ Color',
    fonts: 'Fonts',
    familyEGInter: 'Family (e.g. Inter)',
    roleTitleBody: 'Role (title/body)',
    font: '+ Font',
    logos: 'Logos',
    orALogoUrl: 'or a logo URL',
    addTheUrl: 'Add the URL',
    toneNotesBrandVoice: 'Tone notes / brand voice',
    warmToneInformalAddressAvoidJargon: 'Warm tone, informal address, avoid jargon…',
    default: 'Default',
    fonts2: 'Fonts:',
    edit: 'Edit',
    setAsDefault: 'Set as default',
    delete: 'Delete',
    deleteTheBrandKitName: 'Delete the brand kit “{{name}}”?',
    deletionFailed: 'Deletion failed.',
    updateFailed: 'Update failed.',
    brandKits: 'Brand kits',
    colorsFontsLogosAndBrandTone: 'Colors, fonts, logos and brand tone reusable in the editor.',
    designs: '← Designs',
    newBrandKit2: '+ New brand kit',
    noBrandKits: 'No brand kits',
    createABrandKitToApply: 'Create a brand kit to apply your colors and fonts in one click.',
  },
  components: {
    loading: 'Loading…',
  },
  designEditorPage: {
    timesNewRoman: 'Times New Roman',
    courierNew: 'Courier New',
    trebuchetMs: 'Trebuchet MS',
    noImageFoundOnThisPage: 'No image found on this page.',
    couldNotImportFromThisUrl: 'Could not import from this URL.',
    addAnImage: 'Add an image',
    fromAFile: 'From a file',
    fromAnImageUrl: 'From an image URL',
    httpsImagePng: 'https://…/image.png',
    add: 'Add',
    fromAWebPageOpenGraph: 'From a web page (Open Graph)',
    httpsExampleComArticle: 'https://example.com/article',
    import: 'Import',
    saveAsTemplate: 'Save as template',
    cancel: 'Cancel',
    saving: 'Saving…',
    save: 'Save',
    templateName: 'Template name',
    category: 'Category',
    tagsCommaSeparated: 'Tags (comma-separated)',
    promoSummer: 'promo, summer',
    yourText: 'Your text',
    couldNotLoadTheImageCors: 'Could not load the image (CORS?).',
    imageAddedToTheCanvasBut: 'Image added to the canvas, but saving it to the media library failed.',
    savingTheTemplateFailed: 'Saving the template failed.',
    exportNotPossibleAnExternalImage: 'Export not possible: an external image blocks the export (CORS).',
    sendingToTheMediaLibraryFailed: 'Sending to the media library failed.',
    openingTheDesign: 'Opening the design…',
    backToTheGallery: 'Back to the gallery',
    saveError: 'Save error',
    saved: 'Saved',
    gallery: '← Gallery',
    model: 'Model',
    exporting: 'Exporting…',
    exportPng: 'Export PNG',
    text: '+ Text',
    rectangle: '+ Rectangle',
    circle: '+ Circle',
    line: '+ Line',
    image: '+ Image',
    layers: 'Layers',
    bringToFront: 'Bring to front',
    bringForward: 'Bring forward',
    sendBackward: 'Send backward',
    sendToBack: 'Send to back',
    delete: 'Delete',
    exportYourVisualThenAttachTo: 'Export your visual, then “Attach to the post” to link it to your content.',
    advancedExport: 'Advanced export',
    exportResolutionMultiplier: 'Export resolution (multiplier).',
    exportScale: 'Export scale',
    n1Native: '1× (native)',
    n2HighResolution: '2× (high resolution)',
    n3Print: '3× (print)',
    pngExportedToTheMediaLibrary: 'PNG exported to the media library.',
    attachToThePost: 'Attach to the post →',
    useInAPost: 'Use in a post →',
    properties: 'Properties',
    color: 'Color',
    stroke: 'Stroke',
    opacity: 'Opacity',
    font: 'Font',
    fontSize: 'Font size',
    background: 'Background',
    selectAnObjectToEditIts: 'Select an object to edit its properties.',
    brandKits: 'Brand kits',
    default: ' · default',
    aiAssistant: 'AI assistant',
    fillTheTextsBrief: 'Fill the texts (brief)',
    eGLaunchOurNewSummer: 'E.g. launch our new summer offer…',
    generating: 'Generating…',
    generateTheTexts: 'Generate the texts',
    generateAnImage: 'Generate an image',
    eGMinimalistNeonFox: 'E.g. minimalist neon fox',
    generateTheImage: 'Generate the image',
    suggestAPalette: 'Suggest a palette',
    eGFreshApproachableTechBrand: 'E.g. fresh, approachable tech brand',
  },
  designGalleryPage: {
    openName: 'Open “{{name}}”',
    carousel: 'Carousel',
    custom: 'Custom',
    resizeMagicResize: 'Resize (Magic Resize)',
    duplicate: 'Duplicate',
    delete: 'Delete',
    resizingFailed: 'Resizing failed.',
    resizeName: 'Resize “{{name}}”',
    cancel: 'Cancel',
    resizing: 'Resizing…',
    createTheVariant: 'Create the variant',
    createsANewResizedVariantThe: 'Creates a new resized variant (the original is kept). Every canvas element is scaled automatically.',
    format: 'Format',
    customDimensions: 'Custom dimensions',
    widthPx: 'Width (px)',
    heightPx: 'Height (px)',
    useName: 'Use “{{name}}”',
    system: ' · System',
    deleteTheTemplateName: 'Delete the template “{{name}}”',
    deleteTheTemplate: 'Delete the template',
    use: 'Use',
    newDesign: 'New design',
    name: 'Name',
    creating: 'Creating…',
    create: 'Create',
    creatingTheDesignFailed: 'Creating the design failed.',
    creatingFromTheTemplateFailed: 'Creating from the template failed.',
    duplicationFailed: 'Duplication failed.',
    deleteName: 'Delete “{{name}}”?',
    deletionFailed: 'Deletion failed.',
    designs: 'Designs',
    createVisualsForYourPostsStories: 'Create visuals for your posts, stories and thumbnails.',
    brandKits: 'Brand kits',
    newDesign2: '+ New design',
    myDesigns: 'My designs',
    noDesignsYet: 'No designs yet',
    createOneFromATemplateOr: 'Create one from a template or with the “New design” button.',
    models: 'Models',
    noTemplateAvailable: 'No template available',
    couldNotDeleteTheTemplate: 'Could not delete the template.',
  },
};

export default en;
